/**
 * The one entry gate: may new risk be opened right now? [mv2-D2][S4][S5]
 *
 * It is the worse of the engine's safety verdict (`/monitoring/engine-city/
 * safety`) and the account kill switch (`/system/killswitch`), and it ages
 * both. The Dashboard used to keep the last green for ~183 s through a hung
 * poll (no age check anywhere), read `tripped: null` as not tripped (Ready),
 * and dropped a known trip when a later read failed.
 *
 * `readKillSwitch` interprets the switch for the Dashboard (through this
 * gate), the top-bar emergency stop, the Deploy wizard, the Overview pill and
 * Engine City. It is not yet the only reader: System › Process health reads
 * `kill_switch_tripped` from `/system/health` (null reads unknown, never
 * armed; SystemHealth.tsx), and the Limits workspace's KillSwitchControl
 * reads `/system/killswitch` itself (Monitoring v2 M5 scope). The deployment
 * controls read only its re-arm phrase. A switch last read as tripped stays
 * tripped (last known) through a failed or stale read: unknown safety inputs
 * block new risk, they never reopen it.
 *
 * Pure: runtime imports are relative with `.ts`; types are type-only.
 */
import type { EngineCitySafetyOut, KillSwitchOut } from '@/api/types';
import type { Tone } from '@/lib/colors';

import { formatLocalTime } from '../../lib/format.ts';
import { liveQueryState, type FreshnessClock, type LiveQueryLike } from '../../lib/liveQueryState.ts';

export const KILL_SWITCH_LABELS = { what: 'the kill-switch state', panel: 'Kill switch' } as const;
export const SAFETY_LABELS = { what: 'the safety verdict', panel: 'Safety verdict' } as const;

export type KillState = 'tripped' | 'armed' | 'unknown';
export type KillWhy = 'error' | 'stale' | 'loading' | 'unreadable';

export interface KillReading {
  state: KillState;
  /** Tripped from the last good answer while the latest read failed or went stale. */
  lastKnown: boolean;
  /** Why the state is unknown (or why a trip is only last-known); null when current. */
  why: KillWhy | null;
  /** The last answer, kept through a failed or hung read. */
  data: KillSwitchOut | undefined;
  /** The liveQueryState message (failure, age), or null when current. */
  message: string | null;
  /** When the last good answer arrived. */
  asOfMs: number | null;
  requestId: string | null;
}

/** The kill-switch reading. See the table in the M1 plan, C8. */
export function readKillSwitch(
  q: LiveQueryLike<KillSwitchOut>,
  freshness?: FreshnessClock & { maxAgeMs: number },
): KillReading {
  const s = liveQueryState(q, KILL_SWITCH_LABELS, freshness);
  const data = s.data;
  const tripped = data !== undefined && data !== null && typeof data === 'object' && data.tripped === true;
  const base = { data, message: s.message, asOfMs: s.lastGoodAtMs, requestId: s.requestId };
  switch (s.phase) {
    case 'error-before-data':
    case 'error-after-data':
      return tripped
        ? { ...base, state: 'tripped', lastKnown: true, why: 'error' }
        : { ...base, state: 'unknown', lastKnown: false, why: 'error' };
    case 'overdue-before-data':
    case 'overdue':
      return tripped
        ? { ...base, state: 'tripped', lastKnown: true, why: 'stale' }
        : { ...base, state: 'unknown', lastKnown: false, why: 'stale' };
    case 'idle':
    case 'checking':
    case 'refreshing':
      return tripped
        ? { ...base, state: 'tripped', lastKnown: false, why: null }
        : { ...base, state: 'unknown', lastKnown: false, why: 'loading' };
    case 'live':
      if (tripped) return { ...base, state: 'tripped', lastKnown: false, why: null };
      if (data !== undefined && data !== null && typeof data === 'object' && data.tripped === false) {
        return { ...base, state: 'armed', lastKnown: false, why: null };
      }
      return { ...base, state: 'unknown', lastKnown: false, why: 'unreadable' };
  }
}

export type EntryGateLevel = 'safe' | 'warning' | 'blocked' | 'checking' | 'unknown' | 'unconfirmed';

export const ENTRY_GATE_LABEL: Record<EntryGateLevel, string> = {
  safe: 'Ready',
  warning: 'Degraded',
  blocked: 'Blocked',
  checking: 'Checking',
  unknown: 'Unknown',
  unconfirmed: 'Not confirmed',
};

const TONE: Record<EntryGateLevel, Tone> = {
  safe: 'up', warning: 'warn', blocked: 'danger', checking: 'muted', unknown: 'warn', unconfirmed: 'warn',
};

export interface EntryGate {
  level: EntryGateLevel;
  /** True exactly when level === 'safe'. */
  open: boolean;
  tone: Tone;
  label: string;
  title: string;
  detail: string;
  /** Tooltip only, never the verdict: each source's message, joined with ' · '. */
  note: string;
  reason: string | null;
  untilMs: number | null;
  asOfMs: number | null;
  kill: KillReading;
  retryable: boolean;
}

const SAFETY_LEVELS: readonly string[] = ['safe', 'warning', 'blocked'];
const TREAT_AS_BLOCKED = 'treat new risk as blocked until it answers.';

export function deriveEntryGate({
  safety,
  kill,
  clock,
  safetyMaxAgeMs,
  killMaxAgeMs,
}: {
  safety: LiveQueryLike<EngineCitySafetyOut>;
  kill: LiveQueryLike<KillSwitchOut>;
  clock: FreshnessClock;
  safetyMaxAgeMs: number;
  killMaxAgeMs: number;
}): EntryGate {
  const k = readKillSwitch(kill, { ...clock, maxAgeMs: killMaxAgeMs });
  const s = liveQueryState(safety, SAFETY_LABELS, { ...clock, maxAgeMs: safetyMaxAgeMs });
  const verdict = s.data !== undefined && s.data !== null && typeof s.data === 'object' ? s.data : undefined;

  const notes: string[] = [];
  if (s.message) {
    const lastLevel = verdict !== undefined && typeof verdict.level === 'string' ? verdict.level : null;
    notes.push((s.phase === 'error-after-data' || s.phase === 'overdue') && lastLevel !== null
      ? `${s.message}; last answer: ${lastLevel}` : s.message);
  }
  if (k.message) notes.push(k.lastKnown ? `${k.message}; last known: tripped` : k.message);
  if (k.why === 'unreadable') notes.push('The server reports the kill-switch state as unreadable');
  const note = notes.join(' · ');

  const gate = (
    level: EntryGateLevel,
    title: string,
    detail: string,
    extra: { reason?: string | null; untilMs?: number | null; asOfMs?: number | null; retryable?: boolean } = {},
  ): EntryGate => ({
    level, open: level === 'safe', tone: TONE[level], label: ENTRY_GATE_LABEL[level], title, detail, note,
    reason: extra.reason ?? null, untilMs: extra.untilMs ?? null, asOfMs: extra.asOfMs ?? s.lastGoodAtMs,
    kill: k, retryable: extra.retryable ?? false,
  });

  // 1. A tripped kill switch (current or last known) blocks, whatever else answered.
  if (k.state === 'tripped') {
    const d = k.data;
    return gate('blocked', 'Blocked — the account kill switch is tripped',
      `${d?.trip_reason ?? d?.breach ?? 'A risk limit was breached.'} No new risk until it is reset in Monitoring › Limits.`,
      { reason: 'kill_switch', asOfMs: k.asOfMs, retryable: k.lastKnown });
  }
  // 2. The safety verdict could not be read, or stopped refreshing.
  const unknownTitle = 'Trade readiness unknown';
  if (s.phase === 'error-before-data' || s.phase === 'error-after-data') {
    return gate('unknown', unknownTitle, `The safety verdict could not be read; ${TREAT_AS_BLOCKED}`,
      { reason: 'safety_error', retryable: true });
  }
  if (s.phase === 'overdue') {
    return gate('unknown', unknownTitle,
      `The safety verdict has not refreshed since ${formatLocalTime(s.lastGoodAtMs)}; ${TREAT_AS_BLOCKED}`,
      { reason: 'safety_stale', retryable: true });
  }
  if (s.phase === 'overdue-before-data') {
    return gate('unknown', unknownTitle,
      `The safety verdict has not answered within ${Math.round(safetyMaxAgeMs / 1000)} s; ${TREAT_AS_BLOCKED}`,
      { reason: 'safety_no_answer', retryable: true });
  }
  // 3. Not answered yet, or a cached answer being refreshed.
  if (s.phase === 'checking' || s.phase === 'refreshing' || s.phase === 'idle' || verdict === undefined) {
    return gate('checking', 'Checking whether new trades are allowed…', 'Reading the engine safety verdict.');
  }
  // 4. Malformed backstop: a level outside the union is never read as data.
  if (!SAFETY_LEVELS.includes(verdict.level)) {
    return gate('unknown', unknownTitle, `The safety verdict was malformed; ${TREAT_AS_BLOCKED}`,
      { reason: 'safety_malformed', retryable: true });
  }
  const untilMs = typeof verdict.until_ms === 'number' && Number.isFinite(verdict.until_ms) ? verdict.until_ms : null;
  const withReason = `${verdict.detail}${verdict.reason ? ` (${verdict.reason})` : ''}`;
  // 5. Safe, but the kill switch is not a current, armed reading.
  if (verdict.level === 'safe' && k.state !== 'armed') {
    const detail = k.why === 'loading' ? 'Reading the kill-switch state.'
      : k.why === 'stale' ? `The kill-switch state has not refreshed since ${formatLocalTime(k.asOfMs)}.`
        : 'The kill-switch state could not be read.';
    return gate('unconfirmed', 'Readiness not confirmed', detail,
      { reason: `kill_switch_${k.why ?? 'unknown'}`, retryable: k.why !== 'loading' });
  }
  // 6–8. The engine's own verdict.
  if (verdict.level === 'safe') {
    return gate('safe', 'Ready — new trades are allowed', verdict.detail || 'Every gate is green.',
      { reason: verdict.reason, untilMs });
  }
  if (verdict.level === 'warning') {
    return gate('warning', verdict.headline, withReason, { reason: verdict.reason, untilMs });
  }
  return gate('blocked', verdict.headline, withReason, { reason: verdict.reason, untilMs });
}
